import { useState, type FormEvent, type ReactElement } from 'react';
import { ApiError } from '../shared/api/client';
import { api, WEB_ROLES, type UserView } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { useDepartments } from '../shared/lib/departments';
import { formatPhoneInput } from '../shared/lib/phone';
import {
  PASSWORD_MAX,
  USER_LANGUAGES,
  USER_NAME_MAX,
  emptyUserForm,
  generatePassword,
  generatedLength,
  hasUserErrors,
  toUserCreateInput,
  toUserPatch,
  userErrorsFromProblem,
  userFormFrom,
  validatePassword,
  validateUserForm,
  type UserField,
  type UserFormErrors,
  type UserFormState,
} from '../shared/lib/user-form';
import {
  DISABLED_OPACITY,
  FieldNote,
  FormAlert,
  FormField,
  actionsStyle,
  checkStyle,
  checksStyle,
  describedBy,
  errorStyle,
  fieldsetStyle,
  formStyle,
  hintStyle,
  labelStyle,
  pairStyle,
  supportCode,
} from '../shared/ui/form';
import { Modal as PasswordModal } from '../shared/ui/Modal';
import {
  Drawer,
  primaryButtonStyle,
  secondaryButtonStyle,
  smallButtonStyle,
} from '../shared/ui/primitives';

/**
 * Пользователь кабинета — администратор или логист: заведение, правка, сброс пароля.
 *
 * Телефон — логин и в правке не меняется: смена логина — это новый пользователь, а
 * старая запись остаётся в журнале со своими действиями. Пароль задаётся при
 * заведении и сбрасывается отдельным действием (`POST /web/users/{id}/password`): в
 * общем «Сохранить» он перезаписывался бы при каждой правке имени.
 *
 * Себя администратор не отключает и роль себе не меняет: сервер ответит
 * `cannot_modify_self`, а форма не предлагает этого вовсе.
 */
export function UserDrawer({
  user,
  currentUserId,
  passwordMinLength,
  onClose,
  onSaved,
}: {
  readonly user?: UserView | undefined;
  readonly currentUserId: string;
  readonly passwordMinLength: number | null;
  readonly onClose: () => void;
  readonly onSaved: (user: UserView | null, kind: 'created' | 'saved' | 'password') => void;
}): ReactElement {
  const messages = useMessages();
  const text = messages.cabinet.userForm;
  const departments = useDepartments();
  const editing = user !== undefined;
  const self = user !== undefined && user.id === currentUserId;

  const [original] = useState<UserFormState>(() =>
    user === undefined ? emptyUserForm(departments) : userFormFrom(user),
  );
  const [form, setForm] = useState<UserFormState>(original);
  const [errors, setErrors] = useState<UserFormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [general, setGeneral] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [resetting, setResetting] = useState(false);

  const shownModes = [...new Set([...departments, ...original.modes])];
  const options = { editing, passwordMinLength, originalModes: original.modes };

  const update = (patch: Partial<UserFormState>): void => {
    const next = { ...form, ...patch };

    setForm(next);

    if (submitted) {
      setErrors(validateUserForm(next, options));
    }
  };

  const generalText = (cause: ApiError, self: boolean): string => {
    if (self) {
      return text.errors.cannotModifySelf;
    }

    if (cause.status === FORBIDDEN) {
      return text.errors.forbidden;
    }

    if (cause.code === 'user_is_driver') {
      return text.errors.userIsDriver;
    }

    const base = cause.code === 'validation_failed' ? text.errors.validation : text.errors.failed;

    return `${base} · ${messages.cabinet.drivers.errors.code}: ${supportCode(cause)}`;
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitted(true);
    setGeneral(null);

    const found = validateUserForm(form, options);
    setErrors(found);

    if (hasUserErrors(found)) {
      return;
    }

    setPending(true);

    try {
      if (user === undefined) {
        onSaved(await api.createUser(toUserCreateInput(form)), 'created');
      } else {
        const body = toUserPatch(form, original);

        if (Object.keys(body).length === 0) {
          onClose();

          return;
        }

        onSaved(await api.updateUser(user.id, body), 'saved');
      }
    } catch (cause) {
      if (!(cause instanceof ApiError)) {
        setGeneral(text.errors.failed);

        return;
      }

      const mapped = userErrorsFromProblem(cause);
      setErrors(mapped.fields);

      if (mapped.general) {
        setGeneral(generalText(cause, mapped.self));
      }
    } finally {
      setPending(false);
    }
  };

  const errorOf = (field: UserField): string | null => {
    const code = errors[field];

    if (code === undefined) {
      return null;
    }

    return code === 'passwordTooShort' && passwordMinLength !== null
      ? text.errors.passwordTooShortMin.replace('{min}', String(passwordMinLength))
      : text.errors[code];
  };

  const title = editing ? text.editTitle : text.createTitle;

  return (
    <Drawer
      title={<h2>{title}</h2>}
      label={title}
      onClose={onClose}
      trapFocus
      actions={
        editing ? (
          <button type="button" style={secondaryButtonStyle} onClick={() => setResetting(true)}>
            {text.resetPassword}
          </button>
        ) : null
      }
    >
      <form noValidate onSubmit={(event) => void submit(event)} style={formStyle}>
        <FormField
          id="user-full-name"
          label={text.fullName}
          required={text.required}
          error={errorOf('full_name')}
        >
          <input
            id="user-full-name"
            value={form.full_name}
            maxLength={USER_NAME_MAX}
            autoComplete="off"
            aria-invalid={errors.full_name !== undefined}
            aria-describedby={describedBy('user-full-name', errors.full_name !== undefined)}
            onChange={(event) => update({ full_name: event.target.value })}
          />
        </FormField>

        <FormField
          id="user-phone"
          label={text.phone}
          required={editing ? undefined : text.required}
          hint={editing ? text.phoneLocked : text.phoneHint}
          error={errorOf('phone')}
        >
          <input
            id="user-phone"
            className="num"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={form.phone}
            readOnly={editing}
            aria-invalid={errors.phone !== undefined}
            aria-describedby={describedBy('user-phone', errors.phone !== undefined, true)}
            onChange={(event) => update({ phone: formatPhoneInput(event.target.value) })}
          />
        </FormField>

        <div style={pairStyle}>
          <FormField
            id="user-role"
            label={text.role}
            hint={self ? text.selfLocked : undefined}
            error={errorOf('role')}
          >
            <select
              id="user-role"
              value={form.role}
              disabled={self}
              aria-describedby={describedBy('user-role', errors.role !== undefined, self)}
              onChange={(event) => update({ role: event.target.value as UserFormState['role'] })}
            >
              {WEB_ROLES.map((role) => (
                <option key={role} value={role}>
                  {messages.cabinet.users.roles[role]}
                </option>
              ))}
            </select>
          </FormField>

          <FormField id="user-language" label={text.language} error={null}>
            <select
              id="user-language"
              value={form.language}
              onChange={(event) =>
                update({ language: event.target.value as UserFormState['language'] })
              }
            >
              {USER_LANGUAGES.map((language) => (
                <option key={language} value={language}>
                  {language === 'ru' ? messages.languages.ru : messages.languages.uzLatn}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <fieldset
          style={fieldsetStyle}
          aria-describedby={describedBy('user-modes', errors.modes !== undefined, true)}
        >
          <legend style={labelStyle}>
            {editing ? text.departments : `${text.departments} · ${text.required}`}
          </legend>
          <div style={checksStyle}>
            {shownModes.length === 0 ? (
              <span style={errorStyle}>{messages.cabinet.users.noDepartment}</span>
            ) : null}
            {shownModes.map((mode) => (
              <label key={mode} style={checkStyle}>
                <input
                  type="checkbox"
                  checked={form.modes.includes(mode)}
                  disabled={!departments.includes(mode)}
                  onChange={() =>
                    update({
                      modes: form.modes.includes(mode)
                        ? form.modes.filter((item) => item !== mode)
                        : [...form.modes, mode],
                    })
                  }
                />
                <span>{messages.cabinet.modes[mode]}</span>
              </label>
            ))}
          </div>
          <FieldNote id="user-modes" hint={text.departmentsHint} error={errorOf('modes')} />
        </fieldset>

        {editing ? null : (
          <PasswordField
            id="user-password"
            value={form.password}
            onChange={(password) => update({ password })}
            error={errorOf('password')}
            minLength={passwordMinLength}
          />
        )}

        {editing ? (
          <label style={checkStyle}>
            <input
              type="checkbox"
              checked={form.is_active}
              disabled={self}
              onChange={(event) => update({ is_active: event.target.checked })}
            />
            <span>{text.active}</span>
          </label>
        ) : null}

        <FormAlert text={general} />

        <div style={actionsStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            {messages.cabinet.common.cancel}
          </button>
          <button
            type="submit"
            disabled={pending}
            aria-busy={pending}
            style={{ ...primaryButtonStyle, opacity: pending ? DISABLED_OPACITY : 1 }}
          >
            {pending ? text.saving : editing ? text.save : text.submit}
          </button>
        </div>
      </form>

      {resetting && user !== undefined ? (
        <PasswordDialog
          user={user}
          minLength={passwordMinLength}
          onCancel={() => setResetting(false)}
          onDone={() => {
            setResetting(false);
            onSaved(user, 'password');
          }}
        />
      ) : null}
    </Drawer>
  );
}

/** Пароль с показом и генерацией. Сгенерированный показывается сразу — его надо передать. */
function PasswordField({
  id,
  value,
  onChange,
  error,
  minLength,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error: string | null;
  readonly minLength: number | null;
}): ReactElement {
  const messages = useMessages();
  const text = messages.cabinet.userForm;
  const [visible, setVisible] = useState(false);
  const hint =
    minLength === null
      ? text.passwordHint
      : text.passwordHintMin.replace('{min}', String(minLength));

  return (
    <FormField id={id} label={text.password} required={text.required} hint={hint} error={error}>
      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', minWidth: 0 }}>
        <input
          id={id}
          className="num"
          maxLength={PASSWORD_MAX}
          type={visible ? 'text' : 'password'}
          autoComplete="new-password"
          value={value}
          style={{ flex: '1 1 200px', minWidth: 0 }}
          aria-invalid={error !== null}
          aria-describedby={describedBy(id, error !== null, true)}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          style={{ ...smallButtonStyle, minHeight: 'var(--field-h)' }}
          aria-pressed={visible}
          aria-controls={id}
          onClick={() => setVisible((current) => !current)}
        >
          {visible ? text.hidePassword : text.showPassword}
        </button>
        <button
          type="button"
          style={{ ...smallButtonStyle, minHeight: 'var(--field-h)' }}
          onClick={() => {
            onChange(generatePassword(generatedLength(minLength)));
            setVisible(true);
          }}
        >
          {text.generatePassword}
        </button>
      </div>
    </FormField>
  );
}

/**
 * Сброс пароля — окном поверх панели: действие отдельное от правки и необратимое для
 * пользователя (старый пароль перестаёт работать сразу), поэтому со своим
 * подтверждением и своей ошибкой.
 */
function PasswordDialog({
  user,
  minLength,
  onCancel,
  onDone,
}: {
  readonly user: UserView;
  readonly minLength: number | null;
  readonly onCancel: () => void;
  readonly onDone: () => void;
}): ReactElement {
  const messages = useMessages();
  const text = messages.cabinet.userForm;
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    const violation = validatePassword(password, minLength);

    if (violation !== null) {
      setError(
        violation === 'passwordTooShort' && minLength !== null
          ? text.errors.passwordTooShortMin.replace('{min}', String(minLength))
          : text.errors[violation],
      );

      return;
    }

    setPending(true);
    setError(null);

    try {
      await api.setUserPassword(user.id, password);
      onDone();
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.problem.errors?.some((item) => item.field === 'password') === true &&
            minLength !== null
            ? text.errors.passwordTooShortMin.replace('{min}', String(minLength))
            : `${text.errors.passwordFailed} · ${messages.cabinet.drivers.errors.code}: ${supportCode(cause)}`
          : text.errors.passwordFailed,
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <PasswordModal title={text.resetTitle.replace('{name}', user.full_name)} onCancel={onCancel}>
      <form noValidate onSubmit={(event) => void submit(event)} style={formStyle}>
        <p style={{ margin: 0, ...hintStyle }}>{text.resetDescription}</p>
        <PasswordField
          id="user-new-password"
          value={password}
          onChange={(value) => {
            setPassword(value);
            setError(null);
          }}
          error={error}
          minLength={minLength}
        />
        <div style={actionsStyle}>
          <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
            {messages.cabinet.common.cancel}
          </button>
          <button
            type="submit"
            disabled={pending}
            aria-busy={pending}
            style={{ ...primaryButtonStyle, opacity: pending ? DISABLED_OPACITY : 1 }}
          >
            {pending ? text.saving : text.setPassword}
          </button>
        </div>
      </form>
    </PasswordModal>
  );
}

const FORBIDDEN = 403;
